import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router';
import { Badge, type BadgeTone } from '../../../components/ui/Badge';
import { Button } from '../../../components/ui/Button';
import { Card, CardBody, CardHeader } from '../../../components/ui/Card';
import { Dialog } from '../../../components/ui/Dialog';
import { SelectField, TextAreaField, TextField } from '../../../components/ui/Field';
import { DataTable, ErrorState, TableSkeleton } from '../../../components/ui/Page';
import { useToast } from '../../../components/ui/Toast';
import { formatDate, formatMoney, STATUS_LABELS, type Invoice } from '../../billing/api';
import type { TenantDetail } from '../api';
import { commercialApi, commercialKeys, PAYMENT_METHODS } from '../commercialApi';

const INVOICE_TONE: Record<Invoice['status'], BadgeTone> = { issued: 'warning', paid: 'success', void: 'neutral' };

function MarkPaidDialog({ invoice, onOpenChange }: { invoice: Invoice | null; onOpenChange(open: boolean): void }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [method, setMethod] = useState('bank_transfer');
  const [reference, setReference] = useState('');
  const pay = useMutation({
    mutationFn: () => commercialApi.markPaid(invoice!.id, { method, ...(reference.trim() ? { reference: reference.trim() } : {}) }),
    onSuccess: async () => {
      toast.success('Payment recorded. The subscription is active until the end of the invoice period.');
      setReference('');
      onOpenChange(false);
      await queryClient.invalidateQueries({ queryKey: ['platform'] });
    },
    onError: (err) => toast.error(err),
  });
  return (
    <Dialog
      open={invoice !== null}
      onOpenChange={onOpenChange}
      title={invoice ? `Record payment for ${invoice.number}` : ''}
      description={invoice ? `${formatMoney(invoice.amount, invoice.currency)} for ${invoice.periodStart} – ${invoice.periodEnd}` : undefined}
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button loading={pay.isPending} onClick={() => pay.mutate()}>
            Record payment
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <SelectField
          label="Method"
          value={method}
          onChange={(e) => setMethod(e.target.value)}
          options={Object.entries(PAYMENT_METHODS).map(([value, label]) => ({ value, label }))}
        />
        <TextField label="Reference (optional)" hint="UTR, cheque number…" value={reference} onChange={(e) => setReference(e.target.value)} />
      </div>
    </Dialog>
  );
}

export function InvoiceTable({ invoices, showTenant }: { invoices: Invoice[]; showTenant: boolean }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [paying, setPaying] = useState<Invoice | null>(null);
  const voidIt = useMutation({
    mutationFn: (id: string) => commercialApi.voidInvoice(id, 'Voided from the platform console'),
    onSuccess: async () => {
      toast.success('Invoice voided');
      await queryClient.invalidateQueries({ queryKey: ['platform'] });
    },
    onError: (err) => toast.error(err),
  });
  return (
    <>
      <DataTable
        rows={invoices}
        rowKey={(i) => i.id}
        empty={<p className="p-5 text-sm text-fg-muted">No invoices.</p>}
        columns={[
          { header: 'Invoice', cell: (i) => <span className="font-medium">{i.number}</span> },
          ...(showTenant
            ? [{ header: 'Business', cell: (i: Invoice) => (i.tenant ? <Link to={`/platform/tenants/${i.tenant.id}`}>{i.tenant.name}</Link> : '—') }]
            : []),
          { header: 'Period', cell: (i) => `${i.periodStart} – ${i.periodEnd}` },
          { header: 'Amount', cell: (i) => <span className="tabular-nums">{formatMoney(i.amount, i.currency)}</span> },
          { header: 'Due', cell: (i) => formatDate(i.dueAt) },
          { header: 'Status', cell: (i) => <Badge tone={INVOICE_TONE[i.status]}>{i.status}</Badge> },
          {
            header: '',
            cell: (i) =>
              i.status === 'issued' ? (
                <span className="flex gap-1">
                  <Button size="sm" onClick={() => setPaying(i)}>
                    Mark paid
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => voidIt.mutate(i.id)}>
                    Void
                  </Button>
                </span>
              ) : (
                <span className="text-xs text-fg-subtle">{i.paymentReference ?? ''}</span>
              ),
          },
        ]}
      />
      <MarkPaidDialog invoice={paying} onOpenChange={(o) => !o && setPaying(null)} />
    </>
  );
}

/** Tenant detail: subscription dates and invoices, with "New invoice". */
export function TenantBillingCard({ tenant }: { tenant: TenantDetail }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const invoices = useQuery({
    queryKey: commercialKeys.invoices({ tenantId: tenant.id }),
    queryFn: () => commercialApi.invoices({ tenantId: tenant.id }),
  });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ periodStart: '', periodEnd: '', amount: '', dueDate: '', notes: '' });
  const create = useMutation({
    mutationFn: () =>
      commercialApi.createInvoice(tenant.id, {
        periodStart: form.periodStart,
        periodEnd: form.periodEnd,
        amount: form.amount,
        ...(form.dueDate ? { dueDate: form.dueDate } : {}),
        ...(form.notes.trim() ? { notes: form.notes.trim() } : {}),
      }),
    onSuccess: async () => {
      toast.success('Invoice issued and emailed to the owner');
      setOpen(false);
      await queryClient.invalidateQueries({ queryKey: ['platform'] });
    },
    onError: (err) => toast.error(err),
  });
  const sub = tenant.subscription;
  const set = (k: keyof typeof form) => (e: { target: { value: string } }) => setForm((f) => ({ ...f, [k]: e.target.value }));
  return (
    <Card>
      <CardHeader
        title="Billing"
        description={
          sub
            ? `${STATUS_LABELS[sub.status] ?? sub.status}${sub.trialEndsAt && sub.status === 'trialing' ? `, trial ends ${formatDate(sub.trialEndsAt)}` : ''}${sub.currentPeriodEndsAt ? `, paid until ${formatDate(sub.currentPeriodEndsAt)}` : ''}${sub.graceEndsAt ? `, read-only from ${formatDate(sub.graceEndsAt)}` : ''}`
            : undefined
        }
        actions={
          <Button size="sm" onClick={() => setOpen(true)}>
            New invoice
          </Button>
        }
      />
      {invoices.isPending ? <TableSkeleton rows={2} /> : invoices.isError ? <CardBody><ErrorState error={invoices.error} /></CardBody> : <InvoiceTable invoices={invoices.data} showTenant={false} />}
      <Dialog
        open={open}
        onOpenChange={setOpen}
        title={`New invoice for ${tenant.name}`}
        description="Issued on the current plan. Paying it extends the subscription to the end of the period."
        footer={
          <>
            <Button variant="secondary" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button loading={create.isPending} onClick={() => create.mutate()}>
              Issue invoice
            </Button>
          </>
        }
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField label="Period start" type="date" value={form.periodStart} onChange={set('periodStart')} />
          <TextField label="Period end" type="date" value={form.periodEnd} onChange={set('periodEnd')} />
          <TextField label="Amount" inputMode="decimal" placeholder="2499.00" value={form.amount} onChange={set('amount')} />
          <TextField label="Due date (optional)" type="date" value={form.dueDate} onChange={set('dueDate')} />
          <div className="sm:col-span-2">
            <TextAreaField label="Notes (optional)" value={form.notes} onChange={set('notes')} />
          </div>
        </div>
      </Dialog>
    </Card>
  );
}
